import type {NavFolderMeta, NavPosterMeta} from '@Components/nav-search/nav-search.types';
import {NavSearchEnterOutcome} from '@Components/nav-search/nav-search.types';
import {serializeUrl} from '@Utils/url.util';
import {PosterSectionType} from '@Components/mystuff-top-nav/components/designs-top-bar/designs-top-bar.types';
import {
  getMyStuffPageHash,
  MY_STUFF_HASHES,
  MY_STUFF_POSTER_SECTION_QUERY_KEY,
  MY_STUFF_SEARCH_TERM_QUERY_KEY,
  replaceMyStuffUrlAndNotify,
} from '@Libraries/my-stuff/my-stuff-library';

export interface TemplateSearchMyDesignContentProps {
  searchTerm: string;
  onSelectItem?: (outcome: NavSearchEnterOutcome) => void;
  /** Opt out for the Folders scope, which searches folders alone; every other scope keeps the designs list. */
  showDesignsSection?: boolean;
}

const MAX_FOLDERS_DISPLAY = 2;
/** The folders-only panel has no designs above the list, so it shows as many rows as the designs panel does in total. */
export const MAX_FOLDERS_DISPLAY_WITHOUT_DESIGNS = 5;

export const getUserSearchForMyDesigns = (searchTerm: string): Promise<NavPosterMeta[]> => {
  return window.PMW.readLocal('posters/getNavSearchMyDesignContent', {searchTerm: searchTerm});
};

const isMyStuffDocument = (): boolean => {
  return window.location.pathname.includes('/posters/mine');
};

const getPosterSectionForCurrentPage = (): PosterSectionType => {
  const currentSection = new URLSearchParams(window.location.search).get(MY_STUFF_POSTER_SECTION_QUERY_KEY);
  const pageHash = getMyStuffPageHash();
  const isSharedSection = pageHash === MY_STUFF_HASHES.sharedWithMe || (pageHash === MY_STUFF_HASHES.search && currentSection === PosterSectionType.SHARED_WITH_ME);

  return isSharedSection ? PosterSectionType.SHARED_WITH_ME : PosterSectionType.MY_DESIGNS;
};

export const getMyDesignSearchUrl = (searchTerm: string, posterSectionType: PosterSectionType): URL => {
  const url = new URL(window.PMW.util.site_url('posters/mine'));
  url.searchParams.set(MY_STUFF_SEARCH_TERM_QUERY_KEY, searchTerm);
  url.searchParams.set(MY_STUFF_POSTER_SECTION_QUERY_KEY, posterSectionType);
  url.hash = MY_STUFF_HASHES.search;
  return url;
};

/**
 * Inside My Stuff on mobile the results load in place; elsewhere the results page is a document load.
 */
export const goToMyDesignSearch = (searchTerm: string, canCommitInPlace: boolean): NavSearchEnterOutcome => {
  if (canCommitInPlace && isMyStuffDocument()) {
    replaceMyStuffUrlAndNotify(getMyDesignSearchUrl(searchTerm, getPosterSectionForCurrentPage()));
    return NavSearchEnterOutcome.FILTERED_IN_PLACE;
  }

  window.location.href = serializeUrl(getMyDesignSearchUrl(searchTerm, PosterSectionType.MY_DESIGNS));
  return NavSearchEnterOutcome.NAVIGATING;
};

export const getNavSearchUserFolderContent = (offset: number): Promise<NavFolderMeta[]> => {
  return window.PMW.readLocal('posters/getNavSearchUserFolderContent', {offset: offset});
};

export const filterFoldersBySearchTerm = (searchTerm: string, folders: NavFolderMeta[], maxFolders: number = MAX_FOLDERS_DISPLAY): NavFolderMeta[] => {
  const filteredSearchedFolders = folders.filter((folder) => folder.name.toLowerCase().includes(searchTerm.toLowerCase()));
  return filteredSearchedFolders.sort((folderA, folderB) => {
    return folderB.createdOn - folderA.createdOn;
  }).slice(0, maxFolders);
};
